import React, { useState } from 'react'
import { Sparkles, Plus, AlertCircle, RefreshCw } from 'lucide-react'
import { useCredits } from '@/contexts/CreditContext'
import { Link } from 'react-router-dom'

import { useTranslation } from 'react-i18next';
export const CreditBalanceWidget: React.FC = () => {
    const { t } = useTranslation();
  const { creditData, isLoading, error, refreshCredits } = useCredits()
  const [isHovered, setIsHovered] = useState(false)

  if (error) {
    return (
      <div className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-medium">
        <AlertCircle className="w-4 h-4" />
        <span className="hidden sm:inline">{t('common.error')}</span>
        <button onClick={() => refreshCredits()} className="ml-1 hover:text-red-800 dark:hover:text-red-300">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
    )
  }

  // Format the number to be compact, e.g. 1.2k instead of 1200
  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('en-US', {
      notation: balance > 9999 ? "compact" : "standard",
      compactDisplay: "short"
    }).format(balance)
  }

  const isLowCredits = creditData && creditData.total_balance < 10

  return (
    <div 
      className="relative flex items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link 
        to="/settings?tab=billing"
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border transition-all duration-200 ${
          isLowCredits 
            ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40' 
            : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        } shadow-sm`}
      >
        <div className={`flex items-center justify-center p-1 rounded-full ${
          isLowCredits ? 'bg-amber-100 dark:bg-amber-800/50' : 'bg-primary-100 dark:bg-primary-900/30'
        }`}>
          <Sparkles className={`w-3.5 h-3.5 ${
            isLowCredits ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-400'
          }`} />
        </div>
        
        {isLoading ? (
          <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
        ) : (
          <span className="font-semibold text-sm">
            {creditData ? formatBalance(creditData.total_balance) : '0'}
          </span>
        )}

        <div className={`hidden sm:flex items-center justify-center ml-1 p-0.5 rounded-full ${
          isLowCredits ? 'bg-amber-200 dark:bg-amber-700' : 'bg-gray-100 dark:bg-gray-700'
        } hover:bg-primary-100 hover:text-primary-600 transition-colors`}>
          <Plus className="w-3 h-3" />
        </div>
      </Link>

      {/* Popover */}
      {isHovered && creditData && (
        <div className="absolute top-full right-0 pt-2 z-50">
          <div className="w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-white">{t('settings.aiCredits')}</h4>
              <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 capitalize">
                {creditData.current_tier} Tier
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('settings.billingSection.monthlyAllocation')}</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatBalance(creditData.allocation_balance)}</span>
              </div>
              {creditData.allocation_expires_at && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-1 text-right">
                  Resets {new Date(creditData.allocation_expires_at).toLocaleDateString()}
                </p>
              )}
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('settings.billingSection.topUpBalance')}</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatBalance(creditData.topup_balance)}</span>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-1 text-right">{t('settings.billingSection.neverExpires')}</p>
              
              <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center text-sm font-semibold">
                <span className="text-gray-900 dark:text-white">{t('settings.billingSection.totalBalance')}</span>
                <span className="text-primary-600 dark:text-primary-400">{formatBalance(creditData.total_balance)}</span>
              </div>
            </div>
            
            <Link 
              to="/settings?tab=billing" 
              className="w-full flex items-center justify-center space-x-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('credits.topUpCredits')}</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
